import { useState, useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/v2/ui';
import { changeAspectRatioUpload, type AspectRatioParams } from '@/lib/api/imageOperations';
import { type ImageOptimizeState } from './useImageOptimizeState';
import { AspectRatioSelector } from './AspectRatioSelector';

interface Props {
  state: ImageOptimizeState;
  setState: Dispatch<SetStateAction<ImageOptimizeState>>;
}

type TargetRatio = AspectRatioParams['target_ratio'];
type CropMode = NonNullable<AspectRatioParams['crop_mode']>;

export function AspectRatioGroup({ state, setState }: Props) {
  const { t } = useTranslation();
  const [ratio, setRatio] = useState<TargetRatio>('1:1');
  const [cropMode, setCropMode] = useState<CropMode>('smart');
  const [extendBackground, setExtendBackground] = useState(true);

  const handleApply = useCallback(async () => {
    if (!state.currentFile) return;
    setState(prev => ({ ...prev, isEditing: true }));
    try {
      const result = await changeAspectRatioUpload(state.currentFile, {
        target_ratio: ratio,
        crop_mode: cropMode,
        extend_background: extendBackground,
      });
      if (!result.success) throw new Error(result.error || 'Failed');
      setState(prev => ({
        ...prev,
        isEditing: false,
        editedImageUrl: result.image_url,
        editedS3Key: result.image_id ?? prev.editedS3Key,
        editHistory: [...prev.editHistory, {
          timestamp: new Date(),
          action: 'aspect_ratio',
          labelKey: 'imageOptimize.layout.historyAspectRatio',
          imageUrl: result.image_url,
          s3Key: result.image_id ?? '',
        }],
      }));
    } catch {
      setState(prev => ({ ...prev, isEditing: false }));
    }
  }, [state.currentFile, ratio, cropMode, extendBackground, setState]);

  return (
    <div className="space-y-3">
      <AspectRatioSelector
        ratio={ratio}
        onRatioChange={setRatio}
        cropMode={cropMode}
        onCropModeChange={setCropMode}
        extendBackground={extendBackground}
        onExtendBackgroundChange={setExtendBackground}
        disabled={state.isEditing}
      />
      <Button variant="warning" className="w-full" disabled={!state.currentFile || state.isEditing}
        loading={state.isEditing} onClick={handleApply}>
        {t('imageOptimize.layout.applyRatio')}
      </Button>
    </div>
  );
}
